import { useCallback, useEffect, useMemo, useState } from "react";
import api, { euro } from "../lib/api";
import { toast } from "sonner";
import { Utensils, Plus, Pencil, Trash2, X, Search, FileText, FileSpreadsheet, Package } from "lucide-react";
import { LineChart, Line, XAxis, Tooltip, ResponsiveContainer } from "recharts";

const UNITS = ["pezzo", "kg", "g", "l", "cl", "ml"];
const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k${Math.random().toString(36).slice(2)}`);
const MENU_CATS = ["Antipasti", "Primi", "Secondi", "Contorni", "Pizze", "Dolci", "Bevande", "Altro"];

export default function Recipes() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [threshold, setThreshold] = useState(35);
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => api.get("/recipes").then(({ data }) => setRows(data)).catch(() => toast.error("Impossibile caricare le ricette")), []);
  useEffect(() => {
    load();
    api.get("/catalog/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/settings").then(({ data }) => setThreshold(data?.recipe_food_cost_threshold || data?.food_cost_threshold || 35)).catch(() => {});
  }, [load]);

  const doDelete = async () => { await api.delete(`/recipes/${confirm.id}`); toast.success("Ricetta eliminata"); setConfirm(null); load(); };
  const visible = useMemo(() => rows.filter((r) => r.name.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  const grouped = useMemo(() => {
    const g = {};
    visible.forEach((r) => { const c = r.menu_category || "Altro"; (g[c] = g[c] || []).push(r); });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [visible]);
  const download = async (fmt) => {
    try {
      const ep = fmt === "menu" ? "/recipes/menu.pdf" : `/recipes/report.${fmt}`;
      const fname = fmt === "menu" ? "listino_menu.pdf" : `ricette_food_cost.${fmt}`;
      const res = await api.get(ep, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore nell'export"); }
  };
  const downloadCard = async (id, nm) => {
    try {
      const res = await api.get(`/recipes/${id}/card.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `scheda_${(nm || "piatto").replace(/[^a-z0-9]+/gi, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore nella scheda"); }
  };
  const toProduct = async (r) => {
    try {
      const { data } = await api.post(`/recipes/${r.id}/to-product`);
      toast.success(`Prodotto composto "${data.product.name}" creato in Anagrafica${data.skipped_subrecipes ? ` · ${data.skipped_subrecipes} sotto-ricette ignorate` : ""}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore nella conversione"); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Food cost</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-stone-900">Ricette</h1>
          <p className="text-sm text-stone-500 mt-2">Componi i piatti con gli ingredienti dall'anagrafica. Il costo primario si aggiorna in automatico coi prezzi d'acquisto.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="export-menu-pdf" onClick={() => download("menu")} className="rounded-full border border-stone-300 px-4 py-2.5 text-sm font-semibold text-stone-600 hover:bg-stone-50 flex items-center gap-2"><FileText size={16} /> Listino menu</button>
          <button data-testid="export-recipes-pdf" onClick={() => download("pdf")} className="rounded-full border border-stone-300 px-4 py-2.5 text-sm font-semibold text-stone-600 hover:bg-stone-50 flex items-center gap-2"><FileText size={16} /> PDF</button>
          <button data-testid="export-recipes-xlsx" onClick={() => download("xlsx")} className="rounded-full border border-stone-300 px-4 py-2.5 text-sm font-semibold text-stone-600 hover:bg-stone-50 flex items-center gap-2"><FileSpreadsheet size={16} /> Excel</button>
          <button data-testid="new-recipe-btn" onClick={() => setEdit({ _new: true, name: "", notes: "", menu_price: "", menu_category: "Altro", servings: 1, menu_vat_pct: 10, ingredients: [{ product_id: "", quantity: "", unit_measure: "pezzo" }] })}
            className="rounded-full bg-brand text-white px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-brand-hover transition-colors"><Plus size={16} /> Nuovo piatto</button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input data-testid="recipe-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome piatto…"
          className="w-full border border-stone-300 bg-white pl-9 pr-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
      </div>

      <div className="space-y-6">
        {visible.length === 0 && <div className="bg-white border border-stone-200 rounded-sm py-16 text-center text-stone-400"><Utensils size={28} className="mx-auto mb-2" /> Nessuna ricetta. Crea il tuo primo piatto.</div>}
        {grouped.map(([cat, items]) => {
          const fcs = items.filter((x) => x.food_cost_pct != null).map((x) => x.food_cost_pct);
          const avg = fcs.length ? (fcs.reduce((a, b) => a + b, 0) / fcs.length).toFixed(1) : null;
          return (
            <div key={cat} data-testid={`recipe-cat-${cat}`} className="bg-white border border-stone-200 rounded-sm overflow-hidden">
              <div className="flex items-center justify-between px-6 py-3 bg-stone-50 border-b border-stone-200">
                <span className="font-serif text-lg text-stone-800">{cat}</span>
                {avg != null && <span className="text-xs font-bold text-stone-500">Food cost medio: <span className="text-brand">{avg}%</span></span>}
              </div>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-stone-200 text-xs uppercase tracking-[0.12em] text-stone-500">
                  <th className="text-left py-3 px-6 font-bold">Piatto</th>
                  <th className="text-right py-3 px-6 font-bold">Costo primario</th>
                  <th className="text-right py-3 px-6 font-bold">Prezzo menu</th>
                  <th className="text-right py-3 px-6 font-bold">Food cost %</th>
                  <th className="text-right py-3 px-6 font-bold">Margine</th>
                  <th className="text-right py-3 px-6 font-bold">Azioni</th>
                </tr></thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} data-testid={`recipe-row-${r.id}`} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-4 px-6 font-semibold text-stone-800">{r.name}<div className="text-xs text-stone-400 font-normal">{(r.ingredients || []).length} ingredienti</div></td>
                      <td className="py-4 px-6 text-right text-stone-700">{euro(r.primary_cost || 0)}{r.servings > 1 && r.cost_per_portion != null && <div data-testid={`recipe-portion-${r.id}`} className="text-xs text-stone-400 font-normal">{euro(r.cost_per_portion)}/porz. · {r.servings} porz.</div>}</td>
                      <td className="py-4 px-6 text-right text-stone-700">{r.menu_price != null ? euro(r.menu_price) : "—"}</td>
                      <td data-testid={`recipe-fc-${r.id}`} className="py-4 px-6 text-right">{r.food_cost_pct == null ? <span className="text-stone-400 text-xs">—</span> : <span className={`text-xs font-bold px-2 py-1 rounded-full ${r.food_cost_pct > threshold ? "text-alert bg-alert/10" : "text-brand bg-brand/10"}`}>{r.food_cost_pct}%</span>}</td>
                      <td className="py-4 px-6 text-right text-stone-700">{r.margin_abs != null ? euro(r.margin_abs) : "—"}</td>
                      <td className="py-4 px-6 text-right">
                        <button data-testid={`card-recipe-${r.id}`} onClick={() => downloadCard(r.id, r.name)} className="text-stone-400 hover:text-brand p-1.5" title="Scheda stampabile"><FileText size={15} /></button>
                        <button data-testid={`to-product-recipe-${r.id}`} onClick={() => toProduct(r)} className="text-stone-400 hover:text-indigo-600 p-1.5" title="Crea prodotto composto vendibile"><Package size={15} /></button>
                        <button data-testid={`edit-recipe-${r.id}`} onClick={() => setEdit(r)} className="text-stone-400 hover:text-brand p-1.5"><Pencil size={15} /></button>
                        <button data-testid={`del-recipe-${r.id}`} onClick={() => setConfirm(r)} className="text-stone-400 hover:text-alert p-1.5"><Trash2 size={15} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      {edit && <RecipeDialog r={edit} products={products} recipes={rows} threshold={threshold} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {confirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setConfirm(null)}>
          <div className="bg-white rounded-sm p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl mb-2">Elimina ricetta</h3>
            <p className="text-sm text-stone-600 mb-5">Vuoi eliminare "{confirm.name}"? L'azione è irreversibile.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirm(null)} className="rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-600 hover:bg-stone-50">Annulla</button>
              <button data-testid="confirm-del-recipe" onClick={doDelete} className="rounded-full bg-alert text-white px-4 py-2 text-sm font-semibold hover:opacity-90">Elimina</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RecipeDialog({ r, products, recipes, threshold = 35, onClose, onSaved }) {
  const [f, setF] = useState({ ...r, ingredients: r.ingredients?.length ? r.ingredients.map((i) => ({ product_id: i.product_id || "", recipe_id: i.recipe_id || "", quantity: i.quantity ?? "", unit_measure: i.unit_measure || "pezzo", _key: uid() })) : [{ product_id: "", recipe_id: "", quantity: "", unit_measure: "pezzo", _key: uid() }] });
  const [saving, setSaving] = useState(false);
  const [hist, setHist] = useState([]);
  useEffect(() => {
    if (r.id && !r._new) api.get(`/recipes/${r.id}/history?months=6`).then(({ data }) => setHist(data)).catch(() => {});
  }, [r.id, r._new]);
  const inp = "w-full border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40";
  const prodById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const recipeById = useMemo(() => Object.fromEntries((recipes || []).map((x) => [x.id, x])), [recipes]);

  const setIng = (i, k, v) => setF({ ...f, ingredients: f.ingredients.map((x, idx) => idx === i ? { ...x, [k]: v } : x) });
  const onSelectItem = (i, val) => {
    if (val.startsWith("r:")) {
      const rid = val.slice(2); const rr = recipeById[rid];
      setF({ ...f, ingredients: f.ingredients.map((x, idx) => idx === i ? { ...x, recipe_id: rid, product_id: "", product_name: rr?.name, unit_measure: "porzioni" } : x) });
    } else {
      const pid = val.slice(2); const p = prodById[pid];
      setF({ ...f, ingredients: f.ingredients.map((x, idx) => idx === i ? { ...x, product_id: pid, recipe_id: "", unit_measure: (p?.best_unit_measure) || "pezzo" } : x) });
    }
  };
  const lineCost = (ing) => {
    const qty = Number(String(ing.quantity).replace(",", ".")) || 0;
    if (ing.recipe_id) { const rr = recipeById[ing.recipe_id]; return rr ? (rr.primary_cost || 0) * qty : null; }
    const p = prodById[ing.product_id];
    if (!p || p.best_purchase == null) return null;
    const amt = { kg: 1, g: 0.001, l: 1, cl: 0.01, ml: 0.001, pezzo: 1 }[ing.unit_measure] ?? 1;
    return p.best_purchase * qty * amt;
  };
  const total = f.ingredients.reduce((s, ing) => s + (lineCost(ing) || 0), 0);
  const mp = Number(String(f.menu_price).replace(",", ".")) || 0;
  const fc = mp ? (total / mp * 100) : null;
  const marg = mp ? mp - total : null;
  const vat = Number(String(f.menu_vat_pct ?? 10).replace(",", ".")) || 0;
  const mpNet = mp ? mp / (1 + vat / 100) : null;
  const fcNet = mpNet ? (total / mpNet * 100) : null;
  const margNet = mpNet != null ? mpNet - total : null;

  const save = async () => {
    if (!f.name.trim()) { toast.error("Nome del piatto richiesto"); return; }
    const ingredients = f.ingredients.filter((i) => i.product_id || i.recipe_id).map((i) => {
      const qty = Number(String(i.quantity).replace(",", ".")) || 0;
      if (i.recipe_id) { const rr = recipeById[i.recipe_id]; return { recipe_id: i.recipe_id, product_name: rr?.name, quantity: qty, unit_measure: "porzioni" }; }
      const p = prodById[i.product_id];
      return { product_id: i.product_id, normalized_name: p?.normalized_name, product_name: p?.name, quantity: qty, unit_measure: i.unit_measure || "pezzo" };
    });
    if (ingredients.length === 0) { toast.error("Aggiungi almeno un ingrediente"); return; }
    const body = { name: f.name, notes: f.notes || "", menu_price: f.menu_price === "" || f.menu_price == null ? null : Number(String(f.menu_price).replace(",", ".")), menu_category: f.menu_category || "Altro", servings: Number(String(f.servings).replace(",", ".")) || 1, menu_vat_pct: f.menu_vat_pct === "" || f.menu_vat_pct == null ? 10 : Number(String(f.menu_vat_pct).replace(",", ".")) || 0, ingredients };
    setSaving(true);
    try {
      if (f._new) await api.post("/recipes", body);
      else await api.put(`/recipes/${f.id}`, body);
      toast.success("Ricetta salvata"); onSaved();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 overflow-auto" onClick={onClose}>
      <div data-testid="recipe-dialog" className="bg-white rounded-sm w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <h2 className="font-serif text-2xl">{f._new ? "Nuovo piatto" : "Modifica piatto"}</h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Nome del piatto</label>
              <input data-testid="recipe-name" className={`${inp} mt-1.5`} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Categoria menu</label>
              <select data-testid="recipe-category" className={`${inp} mt-1.5`} value={f.menu_category || "Altro"} onChange={(e) => setF({ ...f, menu_category: e.target.value })}>
                {MENU_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Porzioni rese</label>
              <input data-testid="recipe-servings" type="number" step="any" min="1" className={`${inp} mt-1.5`} value={f.servings ?? 1} onChange={(e) => setF({ ...f, servings: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Prezzo menu €</label>
              <input data-testid="recipe-menu-price" type="number" step="0.01" min="0" className={`${inp} mt-1.5`} value={f.menu_price ?? ""} onChange={(e) => setF({ ...f, menu_price: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">IVA menu %</label>
              <input data-testid="recipe-vat" type="number" step="any" min="0" max="100" className={`${inp} mt-1.5`} value={f.menu_vat_pct ?? 10} onChange={(e) => setF({ ...f, menu_vat_pct: e.target.value })} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500 flex items-center gap-1"><Utensils size={13} /> Ingredienti</label>
              <button data-testid="add-ingredient" onClick={() => setF({ ...f, ingredients: [...f.ingredients, { product_id: "", quantity: "", unit_measure: "pezzo", _key: uid() }] })} className="text-xs text-brand font-semibold hover:underline">+ Aggiungi ingrediente</button>
            </div>
            <div className="space-y-2">
              {f.ingredients.map((ing, i) => {
                const lc = lineCost(ing);
                const subRecipes = (recipes || []).filter((x) => x.id !== f.id);
                return (
                  <div key={ing._key || i} data-testid={`ingredient-${i}`} className="flex gap-2 items-end">
                    <div className="flex-1"><label className="text-[10px] uppercase tracking-wide font-bold text-stone-400">Prodotto o sotto-ricetta</label>
                      <select data-testid={`ing-product-${i}`} className={inp} value={ing.recipe_id ? `r:${ing.recipe_id}` : (ing.product_id ? `p:${ing.product_id}` : "")} onChange={(e) => onSelectItem(i, e.target.value)}>
                        <option value="">— seleziona —</option>
                        <optgroup label="Prodotti">{products.map((p) => <option key={p.id} value={`p:${p.id}`}>{p.name}</option>)}</optgroup>
                        {subRecipes.length > 0 && <optgroup label="Sotto-ricette">{subRecipes.map((x) => <option key={x.id} value={`r:${x.id}`}>🍽 {x.name}</option>)}</optgroup>}
                      </select></div>
                    <div className="w-24"><label className="text-[10px] uppercase tracking-wide font-bold text-stone-400">Quantità</label>
                      <input data-testid={`ing-qty-${i}`} type="number" step="any" min="0" className={inp} value={ing.quantity} onChange={(e) => setIng(i, "quantity", e.target.value)} /></div>
                    <div className="w-24"><label className="text-[10px] uppercase tracking-wide font-bold text-stone-400">Unità</label>
                      {ing.recipe_id ? <div className={`${inp} bg-stone-50 text-stone-500`}>porzioni</div> : (
                        <select data-testid={`ing-unit-${i}`} className={inp} value={ing.unit_measure} onChange={(e) => setIng(i, "unit_measure", e.target.value)}>
                          {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>)}</div>
                    <div className="w-20 text-right pb-2 text-sm font-semibold text-stone-700">{lc != null ? euro(lc) : "—"}</div>
                    <button onClick={() => setF({ ...f, ingredients: f.ingredients.filter((_, idx) => idx !== i) })} className="text-stone-400 hover:text-alert p-2 mb-1"><Trash2 size={15} /></button>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Note (opzionale)</label>
            <textarea data-testid="recipe-notes" rows={2} className={`${inp} mt-1.5`} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
          <div className="flex items-stretch gap-3">
            <div className="flex-1 flex items-center justify-between bg-brand/5 border border-brand/20 rounded-sm px-4 py-3">
              <span className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">Costo primario</span>
              <span data-testid="recipe-primary-cost" className="font-serif text-2xl text-brand">{euro(total)}</span>
            </div>
            <div className="flex-1 flex items-center justify-between bg-stone-50 border border-stone-200 rounded-sm px-4 py-3">
              <span className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">Food cost %</span>
              <span data-testid="recipe-food-cost" className={`font-serif text-2xl ${fc != null && fc > threshold ? "text-alert" : "text-stone-800"}`}>{fc != null ? `${fc.toFixed(1)}%` : "—"}</span>
            </div>
            <div className="flex-1 flex items-center justify-between bg-stone-50 border border-stone-200 rounded-sm px-4 py-3">
              <span className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">Margine</span>
              <span data-testid="recipe-margin" className="font-serif text-2xl text-stone-800">{marg != null ? euro(marg) : "—"}</span>
            </div>
          </div>
          {mp ? (
            <div data-testid="recipe-net-info" className="text-sm text-stone-600 bg-brand/5 border border-brand/20 rounded-sm px-4 py-2.5">
              Al netto IVA ({vat}%): Prezzo <b className="text-stone-800">{euro(mpNet)}</b> · Food cost <b className={fcNet != null && fcNet > threshold ? "text-alert" : "text-brand"}>{fcNet != null ? `${fcNet.toFixed(1)}%` : "—"}</b> · Margine <b className="text-stone-800">{margNet != null ? euro(margNet) : "—"}</b>
            </div>
          ) : null}
          {Number(f.servings) > 1 && (
            <div data-testid="recipe-portion-cost" className="text-sm text-stone-600 bg-stone-50 border border-stone-200 rounded-sm px-4 py-2.5">
              Rende <b>{Number(f.servings)}</b> porzioni · Costo per porzione <b className="text-brand">{euro(total / Number(f.servings))}</b>
              {mp ? <> · Prezzo/porzione <b>{euro(mp / Number(f.servings))}</b></> : null}
            </div>
          )}
          {!f._new && hist.filter((h) => h.cost > 0).length > 1 && (
            <div data-testid="recipe-history">
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Andamento costo primario (6 mesi)</label>
              <div className="mt-2">
                <ResponsiveContainer width="100%" height={130}>
                  <LineChart data={hist}><XAxis dataKey="month" fontSize={10} tickLine={false} /><Tooltip formatter={(v) => euro(v)} /><Line type="monotone" dataKey="cost" stroke="#2C4A3B" strokeWidth={2} dot={{ r: 2 }} /></LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-stone-200">
          <button onClick={onClose} className="rounded-full border border-stone-300 px-5 py-2 text-sm font-semibold text-stone-600 hover:bg-stone-50">Annulla</button>
          <button data-testid="save-recipe-btn" onClick={save} disabled={saving} className="rounded-full bg-brand text-white px-6 py-2 text-sm font-semibold hover:bg-brand-hover disabled:opacity-50">Salva</button>
        </div>
      </div>
    </div>
  );
}
